import { apiFetch } from '@crowdex/host-kit/renderer/http';
import {
  type InstalledPlugin,
  isLocalPluginPreview,
  isUserPluginSource,
  type LocalPluginPreview,
  type PluginCatalogItem,
  type PluginContributionKind,
  pluginContributes,
} from '@crowdex/plugin-contracts';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { WORKSPACE_KEY } from '@/lib/desktop-storage-keys';
import { openDirectoryDialog } from '@/lib/electron-bridge';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';
import type { PluginSection } from '@/lib/plugins/plugin-sections';
import { settingsPreview } from '@/lib/settings-page/settings-preview';
import { CATALOG_PAGE_SIZE } from './catalog';
import { isShownInPlugins } from './plugin-status';
import type { CatalogPage } from './types';

/**
 * The plugins page's state and actions. `pluginName` is the plugin whose page is open (its
 * settings are shown there), `openPlugin` opens one's page.
 */
export function usePluginManagement(
  section: PluginSection,
  pluginName: string | null,
  openPlugin: (name: string) => void,
) {
  const [desktop, setDesktop] = useState(false);
  const [workspaceRoot, setWorkspaceRoot] = useState<string | undefined>();
  const [catalog, setCatalog] = useState<CatalogPage | null>(null);
  const [installed, setInstalled] = useState<InstalledPlugin[]>([]);
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeKind, setActiveKind] = useState<PluginContributionKind | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyPlugin, setBusyPlugin] = useState<string | null>(null);
  const [sourcePath, setSourcePath] = useState<string | null>(null);
  const [sourceContent, setSourceContent] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceSaving, setSourceSaving] = useState(false);
  const [pendingImport, setPendingImport] = useState<{
    path: string;
    preview: LocalPluginPreview;
  } | null>(null);

  useEffect(() => {
    // The preview shows a machine's Plugins page.
    setDesktop(isLocalMachine() || settingsPreview()?.local === true);
    setWorkspaceRoot(window.localStorage.getItem(WORKSPACE_KEY) ?? undefined);
  }, []);

  const loadInstalled = useCallback(async () => {
    setInstalled(await nativePluginClient.list(workspaceRoot));
  }, [workspaceRoot]);

  const loadCatalog = useCallback(async () => {
    const parameters = new URLSearchParams({
      page: String(page),
      pageSize: String(CATALOG_PAGE_SIZE),
      host: isLocalMachine() ? 'desktop' : 'cloud',
    });
    if (activeQuery) parameters.set('query', activeQuery);
    if (activeCategory) parameters.set('category', activeCategory);
    if (activeKind) parameters.set('kind', activeKind);
    setCatalog(await apiFetch<CatalogPage>(`/api/plugins/catalog?${parameters}`));
  }, [activeCategory, activeKind, activeQuery, page]);

  // Why the list could not be read (said with Retry), and the retries asked for.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retryLoad = useCallback(() => setAttempt((value) => value + 1), []);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoadError(null);
    void attempt;
    void Promise.all([section === 'browse' ? loadCatalog() : Promise.resolve(), loadInstalled()])
      .catch((reason) => {
        if (!cancelled) setLoadError(errorMessage(reason));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, loadCatalog, loadInstalled, section]);

  const installedByName = useMemo(
    () => new Map(installed.map((plugin) => [plugin.name, plugin])),
    [installed],
  );
  const ofKind = (plugin: PluginCatalogItem) =>
    activeKind === null || pluginContributes(plugin, activeKind);
  // Model providers and coding agents are wiring (Settings › Models, an assistant's agents): never
  // listed, though installed and on.
  const listed = installed.filter(isShownInPlugins);
  const shownPlugins =
    section === 'installed'
      ? listed.filter(ofKind)
      : section === 'custom'
        ? listed.filter((plugin) => isUserPluginSource(plugin.source))
        : (catalog?.items ?? [])
            .map((plugin) => {
              const local = installedByName.get(plugin.name);
              return local?.source === 'provisioned' ? local : plugin;
            })
            .filter((plugin) => !('crowdex' in plugin) || isShownInPlugins(plugin));
  const configurationPlugin =
    pluginName === null ? null : (installedByName.get(pluginName) ?? null);

  const loadSource = useCallback(
    async (plugin: InstalledPlugin, path: string) => {
      setSourcePath(path);
      setSourceLoading(true);
      setError(null);
      try {
        const source = await nativePluginClient.readSource(plugin.name, path, workspaceRoot);
        setSourceContent(source.content);
      } catch (reason) {
        setError(errorMessage(reason));
      } finally {
        setSourceLoading(false);
      }
    },
    [workspaceRoot],
  );

  useEffect(() => {
    if (configurationPlugin === null) {
      setSourcePath(null);
      setSourceContent('');
      return;
    }
    const firstSource = configurationPlugin.sourceFiles?.[0];
    if (firstSource === undefined) {
      setSourcePath(null);
      setSourceContent('');
      return;
    }
    if (!configurationPlugin.sourceFiles?.some((source) => source.path === sourcePath)) {
      void loadSource(configurationPlugin, firstSource.path);
    }
  }, [configurationPlugin, loadSource, sourcePath]);

  function searchCatalog(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setActiveQuery(query.trim());
  }

  async function runAction(name: string, action: () => Promise<void>) {
    setBusyPlugin(name);
    setError(null);
    try {
      await action();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusyPlugin(null);
    }
  }

  /** Turns one contribution of an installed plugin on or off. */
  async function setContributionEnabled(plugin: InstalledPlugin, key: string, enabled: boolean) {
    await runAction(plugin.name, async () => {
      await nativePluginClient.setContributionEnabled(plugin.name, key, enabled, workspaceRoot);
      await loadInstalled();
    });
  }

  /** Lets a command of a plugin run without a sandbox on this Windows machine, or stops it. */
  async function setCommandUnsandboxed(plugin: InstalledPlugin, command: string, allowed: boolean) {
    await runAction(plugin.name, async () => {
      await nativePluginClient.setCommandUnsandboxed(plugin.name, command, allowed);
      await loadInstalled();
    });
  }

  async function install(plugin: PluginCatalogItem) {
    await runAction(plugin.name, async () => {
      await nativePluginClient.install(plugin);
      await loadInstalled();
    });
  }

  /**
   * Imports a folder of this machine (`path`, or one the user picks). A `crowdex.json` folder
   * waits in `pendingImport` until the user approves what it may do (`confirmImport`).
   */
  async function importLocal(path?: string) {
    if (!desktop) return;
    const folder = path?.trim() || (await openDirectoryDialog());
    if (!folder) return;
    await runAction(folder, async () => {
      const result = await nativePluginClient.installLocal(folder);
      if (isLocalPluginPreview(result)) {
        setPendingImport({ path: folder, preview: result });
        return;
      }
      await loadInstalled();
      openPlugin(result.name);
    });
  }

  async function confirmImport() {
    if (pendingImport === null) return;
    const { path, preview } = pendingImport;
    await runAction(preview.name, async () => {
      await nativePluginClient.installLocal(path, preview.grants);
      setPendingImport(null);
      await loadInstalled();
      openPlugin(preview.name);
    });
  }

  async function refreshLocal(plugin: InstalledPlugin) {
    const path = plugin.localSourcePath;
    if (path === undefined) return;
    await runAction(plugin.name, async () => {
      // A `crowdex.json` folder is built again as it is read; one asking for more waits for the
      // user's approval on its page.
      if (plugin.crowdex === undefined) await nativePluginClient.installLocal(path);
      await loadInstalled();
    });
  }

  /** Installs the waiting update of a plugin, approving what it adds. */
  async function approveUpdate(plugin: InstalledPlugin) {
    await runAction(plugin.name, async () => {
      await nativePluginClient.approveUpdate(plugin.name);
      await loadInstalled();
    });
  }

  /** Uploads a folder plugin to the Crowdex Cloud account and installs it there. */
  async function publish(plugin: InstalledPlugin) {
    await runAction(plugin.name, async () => {
      await nativePluginClient.publish(plugin.name);
      await loadInstalled();
    });
  }

  async function saveSource(plugin: InstalledPlugin) {
    if (sourcePath === null) return;
    setSourceSaving(true);
    setError(null);
    try {
      await nativePluginClient.writeSource(plugin.name, sourcePath, sourceContent, workspaceRoot);
      await loadInstalled();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setSourceSaving(false);
    }
  }

  async function toggle(plugin: InstalledPlugin) {
    await runAction(plugin.name, async () => {
      await nativePluginClient.setEnabled(plugin.name, !plugin.enabled, workspaceRoot);
      await loadInstalled();
    });
  }

  /** Deletes a group of this computer's skills (its folders and lock entries) once confirmed. */
  async function removeMachineSkills(plugin: InstalledPlugin) {
    const folders = plugin.machineSkills?.folders ?? [];
    if (!desktop || folders.length === 0) return;
    await runAction(plugin.name, async () => {
      await nativePluginClient.removeMachineSkills(folders);
      await loadInstalled();
    });
  }

  async function uninstall(plugin: InstalledPlugin) {
    if (!window.confirm(`Uninstall ${plugin.displayName}? You can install it again from Browse.`))
      return;
    await runAction(plugin.name, async () => {
      await nativePluginClient.uninstall(plugin.name, workspaceRoot);
      await loadInstalled();
    });
  }

  return {
    workspaceRoot,
    activeCategory,
    activeKind,
    reloadCatalog: loadCatalog,
    setActiveKind,
    setContributionEnabled,
    setCommandUnsandboxed,
    busyPlugin,
    catalog,
    configurationPlugin,
    desktop,
    error,
    loadError,
    retryLoad,
    importLocal,
    confirmImport,
    pendingImport,
    cancelImport: () => setPendingImport(null),
    approveUpdate,
    publish,
    install,
    installed,
    /** What Settings › Plugins lists of `installed`: all but hidden wiring. */
    listed,
    installedByName,
    loadSource,
    loading,
    page,
    query,
    refreshLocal,
    removeMachineSkills,
    saveSource,
    searchCatalog,
    setActiveCategory,
    setPage,
    setQuery,
    setSourceContent,
    shownPlugins,
    sourceContent,
    sourceLoading,
    sourcePath,
    sourceSaving,
    toggle,
    uninstall,
  };
}
